import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { link } from 'chaoskit/src/assets/styles/utility'

import Data from '~components/Data'
import TextLink from '~components/TextLink'
import Image from '~components/Image'

// Supports both local-queried and Algolia-sourced blog teasers
export default function TeaserCard({ data, title, formatDate = true }) {
  const theme = useTheme()

  const dateFormatter = new Date(data.date * 1000).toLocaleString('en', {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <TextLink
      href={`${data.slug}${data.contentId ? `#${data.contentId}` : ''}`}
      css={[
        link.reset(theme),
        {
          borderTopLeftRadius: theme.borderRadius.base,
          borderTopRightRadius: theme.borderRadius.base,
          position: 'relative',
          zIndex: 1,

          '&::after': {
            content: "''",
            zIndex: -1,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 300,
            background: `linear-gradient(${theme.color.light.base}, ${theme.color.dark.base}, ${theme.color.light.base})`,
            opacity: theme.opacity.base / 2,
            filter: 'blur(4px)',
            willChange: 'filter',
            transition: `filter ${theme.timing.base} ${theme.transition.base}`,
          },

          '&:hover, &:focus': {
            '&::after': {
              filter: 'blur(8px)',
              opacity: theme.opacity.base,
            },
          },
        },
      ]}
    >
      <div
        css={{
          height: 0,
          paddingBottom: '50%',
          background: theme.color.dark.base,
          position: 'relative',
          borderTopLeftRadius: theme.borderRadius.base,
          borderTopRightRadius: theme.borderRadius.base,
          overflow: 'hidden',
        }}
      >
        <Image
          src={data.featuredImage}
          css={{
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectPosition: 'right bottom',
          }}
        />
      </div>

      <div
        css={{ padding: theme.space.base, background: theme.color.light.base }}
      >
        <div
          css={{
            ...theme.text.large,
            fontWeight: theme.fontWeight.bold,
            margin: 0,
          }}
        >
          {title}
        </div>
        {(data.date || data.timeToRead) && (
          <div
            css={{
              color: theme.fontColor.muted,
              ...theme.text.xsmall,
              marginTop: theme.space.small,
            }}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {data.date ? (formatDate ? dateFormatter : data.date) : null}{' '}
            {data.timeToRead && `- ${data.timeToRead} min read`}
          </div>
        )}
        <div css={{ marginTop: theme.space.small, ...theme.text.small }}>
          <Data>{data.excerpt}</Data>
        </div>
      </div>
    </TextLink>
  )
}

TeaserCard.propTypes = {
  data: PropTypes.shape({
    contentId: PropTypes.string,
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    excerpt: PropTypes.string.isRequired,
    featuredImage: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    timeToRead: PropTypes.number,
  }),
  formatDate: PropTypes.bool,
  // This is pulled away from the `data` shape since we need to wrap it with the Algolia search provider funcs in `algolia-blog`
  title: PropTypes.any.isRequired,
}
